import { createFileRoute, Link } from '@tanstack/react-router'
import React from 'react'
import { ArrowLeft, BarChart2, Download } from 'lucide-react'
import {
  AnalyticsPerformanceDashboard,
  exampleMetrics,
  generateFakeTimeSeriesData
} from '@/components/AnalyticsPerformanceDashboard'

export const Route = createFileRoute('/_auth/design-system/analytics-dashboard')({
  component: AnalyticsDashboardPage,
})

function AnalyticsDashboardPage() {
  const timeSeriesData = generateFakeTimeSeriesData(7)

  return (
    <div className="min-h-screen bg-brand-dark text-white">
      {/* Header */}
      <div className="bg-brand-dark-light border-b border-brand-gray/20">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center space-x-4 mb-6">
            <button
              onClick={() => window.history.back()}
              className="p-2 rounded-monumenta bg-brand-dark hover:bg-brand-dark-lighter transition-colors duration-200 text-brand-gray hover:text-white"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-3xl font-display font-bold text-brand-primary">
                Analytics Dashboard
              </h1>
              <p className="text-brand-gray">
                Sistema de métricas interativas com gráficos de tendência temporal
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">

        {/* Introduction */}
        <div className="mb-12">
          <div className="bg-brand-dark-light rounded-monumenta p-8 border border-brand-gray/10">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-brand-primary/10 rounded-monumenta">
                <BarChart2 className="text-brand-primary" size={32} />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-display font-semibold text-white mb-3">
                  Dashboard de Performance Analítica
                </h2>
                <p className="text-brand-gray mb-4">
                  Este componente combina cards de métricas clicáveis com gráficos de linha temporal,
                  inspirado no padrão usado pelo Google Search Console. Clique nos cards de métricas
                  para ativar/desativar as linhas correspondentes no gráfico.
                </p>
                <div className="flex items-center gap-3">
                  <div className="status-primary">Interativo</div>
                  <div className="status-success">Multi-métrica</div>
                  <div className="status-info">Temporal</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Live Demo */}
        <div className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-display font-semibold text-white">
              Demo Interativo
            </h2>
            <button className="btn-ghost btn-sm">
              <Download size={14} className="mr-1" />
              Exportar Dados
            </button>
          </div>

          <AnalyticsPerformanceDashboard
            metrics={exampleMetrics}
            timeSeriesData={timeSeriesData}
            title="Performance dos Últimos Meses"
            dateRange="Janeiro - Julho 2025"
          />
        </div>

        {/* Design Principles */}
        <div className="mb-16">
          <h2 className="text-2xl font-display font-semibold text-white mb-6">
            Princípios de Design
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="card">
              <div className="mb-4">
                <div className="w-12 h-12 bg-brand-primary/10 rounded-monumenta flex items-center justify-center mb-3">
                  <span className="text-2xl">👆</span>
                </div>
                <h3 className="text-lg font-display font-semibold text-white mb-2">
                  Interatividade Clara
                </h3>
              </div>
              <p className="text-brand-gray text-sm">
                Cards de métricas funcionam como toggles - clique para ativar/desativar
                a visualização da métrica no gráfico temporal.
              </p>
            </div>

            <div className="card">
              <div className="mb-4">
                <div className="w-12 h-12 bg-success/10 rounded-monumenta flex items-center justify-center mb-3">
                  <span className="text-2xl">📊</span>
                </div>
                <h3 className="text-lg font-display font-semibold text-white mb-2">
                  Contexto Visual
                </h3>
              </div>
              <p className="text-brand-gray text-sm">
                O gráfico de linha fornece contexto histórico imediato para cada métrica,
                mostrando tendências e padrões ao longo do tempo.
              </p>
            </div>

            <div className="card">
              <div className="mb-4">
                <div className="w-12 h-12 bg-brand-blue/10 rounded-monumenta flex items-center justify-center mb-3">
                  <span className="text-2xl">🎨</span>
                </div>
                <h3 className="text-lg font-display font-semibold text-white mb-2">
                  Cores Consistentes
                </h3>
              </div>
              <p className="text-brand-gray text-sm">
                Cada métrica tem uma cor dedicada que é mantida entre o card e
                sua representação no gráfico, facilitando a correlação visual.
              </p>
            </div>
          </div>
        </div>

        {/* Use Cases */}
        <div className="mb-16">
          <h2 className="text-2xl font-display font-semibold text-white mb-6">
            Casos de Uso
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card-hover">
              <h3 className="text-lg font-display font-semibold text-white mb-3">
                Dashboard de Performance de Vendas
              </h3>
              <ul className="space-y-2 text-brand-gray text-sm">
                <li>• Receita total vs meta mensal</li>
                <li>• Número de deals fechados</li>
                <li>• Taxa de conversão</li>
                <li>• Ticket médio por cliente</li>
              </ul>
            </div>

            <div className="card-hover">
              <h3 className="text-lg font-display font-semibold text-white mb-3">
                Analytics de Produto
              </h3>
              <ul className="space-y-2 text-brand-gray text-sm">
                <li>• Usuários ativos diários/mensais</li>
                <li>• Taxa de retenção</li>
                <li>• Feature adoption rate</li>
                <li>• Customer satisfaction score</li>
              </ul>
            </div>

            <div className="card-hover">
              <h3 className="text-lg font-display font-semibold text-white mb-3">
                Gestão de RH
              </h3>
              <ul className="space-y-2 text-brand-gray text-sm">
                <li>• Total de colaboradores ativos</li>
                <li>• Taxa de turnover</li>
                <li>• Satisfação dos funcionários</li>
                <li>• Produtividade por departamento</li>
              </ul>
            </div>

            <div className="card-hover">
              <h3 className="text-lg font-display font-semibold text-white mb-3">
                Marketing Digital
              </h3>
              <ul className="space-y-2 text-brand-gray text-sm">
                <li>• Impressões e cliques</li>
                <li>• CTR médio</li>
                <li>• Custo por aquisição</li>
                <li>• ROAS (Return on Ad Spend)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Component API */}
        <div className="mb-16">
          <h2 className="text-2xl font-display font-semibold text-white mb-6">
            API do Componente
          </h2>

          <div className="bg-brand-dark-light rounded-monumenta border border-brand-gray/10 overflow-hidden">
            <div className="p-6 border-b border-brand-gray/20">
              <h3 className="font-display font-semibold text-white mb-2">
                AnalyticsPerformanceDashboard
              </h3>
              <p className="text-brand-gray text-sm">
                Componente principal que combina metric cards com trend chart
              </p>
            </div>

            <div className="p-6">
              <div className="space-y-4 font-mono text-sm">
                <div>
                  <span className="text-brand-primary">metrics</span>
                  <span className="text-brand-gray">: MetricDefinition[]</span>
                  <p className="text-brand-gray text-xs ml-4 mt-1">Array de definições de métricas</p>
                </div>
                <div>
                  <span className="text-brand-primary">timeSeriesData</span>
                  <span className="text-brand-gray">: TimeSeriesDataPoint[]</span>
                  <p className="text-brand-gray text-xs ml-4 mt-1">Dados temporais para o gráfico</p>
                </div>
                <div>
                  <span className="text-brand-primary">title</span>
                  <span className="text-brand-gray">?: string</span>
                  <p className="text-brand-gray text-xs ml-4 mt-1">Título do dashboard (opcional)</p>
                </div>
                <div>
                  <span className="text-brand-primary">dateRange</span>
                  <span className="text-brand-gray">?: string</span>
                  <p className="text-brand-gray text-xs ml-4 mt-1">Range de datas exibido (opcional)</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Code Example */}
        <div className="mb-16">
          <h2 className="text-2xl font-display font-semibold text-white mb-6">
            Exemplo de Código
          </h2>

          <div className="bg-brand-dark rounded-monumenta p-6 border border-brand-gray/10 overflow-x-auto">
            <pre className="text-brand-gray text-sm">
              <code>{`import {
  AnalyticsPerformanceDashboard,
  exampleMetrics,
  generateFakeTimeSeriesData
} from '@/components/AnalyticsPerformanceDashboard'

function MyDashboard() {
  const metrics = exampleMetrics
  const data = generateFakeTimeSeriesData(7)

  return (
    <AnalyticsPerformanceDashboard
      metrics={metrics}
      timeSeriesData={data}
      title="Performance Overview"
      dateRange="Last 7 months"
    />
  )
}`}</code>
            </pre>
          </div>
        </div>

        {/* Best Practices */}
        <div className="mt-20">
          <div className="bg-brand-dark-light rounded-monumenta p-8 border border-brand-gray/10">
            <h3 className="text-2xl font-display font-semibold text-brand-primary mb-6">
              Boas Práticas de Uso
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-display font-semibold text-success mb-4">
                  ✅ Recomendado
                </h4>
                <ul className="space-y-3 text-brand-gray">
                  <li>• Limite a 4-6 métricas por dashboard</li>
                  <li>• Use cores distintas e consistentes</li>
                  <li>• Mostre variação percentual quando relevante</li>
                  <li>• Mantenha labels claros e concisos</li>
                  <li>• Forneça contexto temporal adequado</li>
                  <li>• Permita export de dados</li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-display font-semibold text-error mb-4">
                  ❌ Evitar
                </h4>
                <ul className="space-y-3 text-brand-gray">
                  <li>• Mais de 6 linhas simultâneas no gráfico</li>
                  <li>• Cores muito similares entre métricas</li>
                  <li>• Labels técnicos sem contexto</li>
                  <li>• Escalas inconsistentes entre métricas</li>
                  <li>• Omitir unidades de medida</li>
                  <li>• Gráficos sem interatividade</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
